import changeSwipeGameIcon from '../../entities/changeSwipeGameIcon';
import findGameInTheList from '../../entities/findGameInTheList';
import playGame from '../../features/playGames';
import eventType from '../enums/eventType';
import { LocalStoreKeys } from '../enums/localStoreKeys';
import eventEmitters from '../lib/eventEmitters';
import { getSDKConfig } from '../lib/getSDKConfig';
import { setLocalStore } from '../lib/localStore';
import { SwipeGamesItem } from '../model/swipe';
import { SelectedGame } from '../types/SwipeSDKConfigType';
import { createIframe } from './createIframe';
import { GameIconManager } from './gameIconManager';
import { GameGridManager } from './gameGridManager';
import getElementSelectors from '../lib/getElementSelectors';
import { applyGameGradient } from '../../features/applyGameGradient';

export class SwipeMenu {
  private static readonly SCROLL_DELAY_MS = 50;
  private static readonly INITIAL_SCROLL_DELAY_MS = 50;

  private container: HTMLElement | null = null;
  private icons: SwipeGamesItem[] = [];
  private gameIconManager: GameIconManager | null = null;
  private gameGridManager: GameGridManager | null = null;

  public init(containerId = 'scrollable-icons'): void {
    const initFn = () => this.setup(containerId);

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initFn);
    } else {
      initFn();
    }
  }

  private setup(containerId: string): void {
    this.container = this.findContainer(containerId);
    this.removeDropdown();

    if (!this.container) return;

    this.icons = getSDKConfig()?.swipeGames?.gameListConfig?.gameList || [];

    this.initializeManagers();
    this.setupSwipeControlResizeListener();
    this.render();
    applyGameGradient(getSDKConfig()?.swipeGames?.iconPosition || undefined);
  }

  private removeDropdown(): void {
    const { dropdown, dropDownOverlay } = getElementSelectors();
    dropdown?.remove();
    dropDownOverlay?.remove();
  }

  private findContainer(containerId: string): HTMLElement | null {
    return (
      document.querySelector(`.${containerId}`) ||
      document.getElementById(containerId)
    );
  }

  private initializeManagers(): void {
    this.gameIconManager = new GameIconManager(this.container, this.icons);
    this.gameGridManager = new GameGridManager(this.icons);

    this.gameIconManager.setOnIconClick((icon, index) =>
      this.handleIconClick(icon, index),
    );
    this.gameGridManager.setOnGameGridClick((gameIndex, gameId) =>
      this.handleGameGridClick(gameIndex, gameId),
    );
  }

  private render(): void {
    if (!this.container || !this.gameIconManager || !this.gameGridManager)
      return;

    this.gameIconManager.setDefaultActiveIcon();
    this.updateMenuAction();
    this.gameIconManager.renderAllIcons();
    this.gameIconManager.updateIconVisibility();

    this.gameGridManager.populateGameGrid();
    this.gameGridManager.initializeGameGrid();

    const activeIndex = this.gameIconManager.getActiveIconIndex();
    if (activeIndex !== -1) {
      this.gameGridManager.updateGameGridSelection(activeIndex);
      setTimeout(() => {
        this.gameIconManager?.scrollIconIntoView(activeIndex);
      }, SwipeMenu.INITIAL_SCROLL_DELAY_MS);
    }
  }

  public setIcons(icons: SwipeGamesItem[]): void {
    this.icons = icons;

    if (this.container) {
      this.initializeManagers();
    }

    this.render();
  }

  private updateMenuAction(): void {
    const menuActionContainer = document.getElementById('sdk-menu-action');
    if (!menuActionContainer) return;

    if (this.shouldShowHamburgerMenu()) {
      this.renderHamburgerMenu(menuActionContainer);
    } else {
      menuActionContainer.innerHTML = '';
    }
  }

  private shouldShowHamburgerMenu(): boolean {
    if (!this.gameIconManager) return false;
    return this.gameIconManager.shouldShowHamburgerMenu();
  }

  private renderHamburgerMenu(container: HTMLElement): void {
    container.innerHTML = this.getHamburgerMenuHTML();
    this.setupDrawerToggle();
  }

  private getHamburgerMenuHTML(): string {
    return `
      <div class="menu-button-container" id="menu-toggle">
        <div class="hamburger-icon">
          <span></span>
          <span></span>
          <span></span>
        </div>
      </div>
    `;
  }

  private setupDrawerToggle(): void {
    const drawer = document.querySelector('.swipe-sdk-drawer');
    const overlay = document.querySelector('.drawer-overlay');

    if (!drawer || !overlay) return;

    const toggleDrawer = () => {
      const isOpen = drawer.classList.contains('open');

      if (isOpen) {
        // Close drawer with animation
        drawer.classList.remove('open');
        drawer.classList.add('closing');
        overlay.classList.remove('open');
        setTimeout(() => {
          drawer.classList.remove('closing');
        }, 300);
      } else {
        // Open drawer
        drawer.classList.remove('closing');
        drawer.classList.add('open');
        overlay.classList.add('open');
      }

      eventEmitters(
        !isOpen ? eventType.SWIPE_DRAWER_OPEN : eventType.SWIPE_DRAWER_CLOSED,
      );
    };

    const elements = [
      document.getElementById('menu-toggle'),
      document.getElementById('close-drawer-btn'),
      overlay,
    ];

    for (const element of elements) {
      element?.addEventListener('click', toggleDrawer);
    }
  }

  private async handleGameSelection(
    gameId: string,
    event: string,
  ): Promise<void> {
    const foundGame = findGameInTheList(gameId);

    const currentGameselected: SelectedGame = {
      currentGameId: gameId,
    };
    changeSwipeGameIcon(currentGameselected);
    setLocalStore(LocalStoreKeys.SELECTED_GAME, currentGameselected);

    const gameLaunchUrl = foundGame ? await playGame() : null;
    if (gameLaunchUrl) {
      createIframe(gameLaunchUrl);
    }
    eventEmitters(event);
  }

  private async handleIconClick(
    icon: SwipeGamesItem,
    index: number,
  ): Promise<void> {
    this.setActiveIcon(index);

    const gameId = icon.gameId?.toString();

    if (gameId) {
      await this.handleGameSelection(gameId, eventType.SWIPE_TOPBAR_GAMECHANGE);
    }
  }

  public setActiveIcon(index: number): void {
    if (!this.gameIconManager?.isValidIndex(index)) return;

    const wasChanged = this.gameIconManager.getActiveIconIndex() !== index;
    if (!wasChanged) return;

    this.gameIconManager.setActiveIconIndex(index);
    this.gameIconManager.updateIconVisibility();

    setTimeout(() => {
      this.gameIconManager?.scrollIconIntoView(index);
    }, SwipeMenu.SCROLL_DELAY_MS);

    this.gameGridManager?.updateGameGridSelection(index);
  }

  private async handleGameGridClick(
    gameIndex: number,
    gameId: string,
  ): Promise<void> {
    if (!this.gameGridManager) return;

    this.gameGridManager.closeDrawer();
    this.setActiveIcon(gameIndex);
    this.gameIconManager?.scrollIconIntoView(gameIndex);

    if (gameId) {
      await this.handleGameSelection(gameId, eventType.SWIPE_DRAWER_GAMECHANGE);
    }
  }

  public getActiveIcon(): SwipeGamesItem | null {
    return this.gameIconManager?.getActiveIcon() ?? null;
  }

  public clearActiveIcon(): void {
    if (!this.gameIconManager) return;

    this.gameIconManager.setActiveIconIndex(-1);
    this.gameIconManager.updateIconVisibility();
  }

  private setupSwipeControlResizeListener(): void {
    const { swipe_control } = getElementSelectors();

    if (swipe_control) {
      window.addEventListener('resize', () => {
        const swipeYPos = sessionStorage.getItem('swipeYPos');
        if (swipeYPos) {
          swipe_control.style.top = `${Number(swipeYPos)}%`;
        }
        const vh = (window.innerHeight - 64) * 0.01;
        document.documentElement.style.setProperty('--vh', `${vh}px`);
      });
    }
  }
}

export const swipeMenus = new SwipeMenu();
